// P36 (2026-09-25): página PÚBLICA con el resultado de la autorización de QuickBooks.
// El backend (GET /api/quickbooks/callback) manda acá al terminar: ?ok=1&empresa=... si quedó
// conectada, o ?error=... si no. La ve el administrador de QuickBooks, que abre el enlace que le
// mandaron y NO tiene usuario en la plataforma: por eso vive fuera de <Protected> en App.js y no
// llama a la API. Antes el callback mandaba a /config, que sin sesión termina en /login: la persona
// no sabía si funcionó, y un enlace vencido se veía igual que un éxito.
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Truck, XCircle } from "lucide-react";

const CONTACTO_URL = "https://olimpo.tech";

// Errores que puede mandar el backend o Intuit, en palabras de la persona que abrió el enlace.
const MENSAJES = {
  estado_invalido_o_expirado: {
    titulo: "El enlace venció o ya se usó",
    texto: "Cada enlace sirve una sola vez y dura 60 minutos desde que se genera. Pedí un enlace nuevo a quien te lo mandó y abrilo dentro de esa hora.",
  },
  access_denied: {
    titulo: "Se canceló la autorización en Intuit",
    texto: "No se conectó nada. Si fue sin querer, abrí el mismo enlace de nuevo (si todavía no venció) y elegí la empresa correcta.",
  },
  falta_code_o_realmId: {
    titulo: "Intuit no devolvió los datos de la empresa",
    texto: "No se conectó nada. Pedí un enlace nuevo e intentá otra vez.",
  },
};

function mensajeDe(error) {
  if (MENSAJES[error]) return { ...MENSAJES[error], detalle: null };
  return {
    titulo: "No se pudo completar la conexión",
    texto: "No se conectó nada. Mandale este detalle a quien te pasó el enlace para que lo revise:",
    detalle: error,
  };
}

export default function ConexionQuickBooks() {
  const [params] = useSearchParams();
  const ok = params.get("ok") === "1";
  const empresa = (params.get("empresa") || "").trim();
  const error = params.get("error") || "";
  const m = ok ? null : mensajeDe(error || "sin_resultado");

  return (
    <div className="min-h-screen bg-white" data-testid="pagina-conexion-quickbooks">
      <div className="mx-auto max-w-xl px-6 py-10">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Truck className="h-5 w-5" />
          </div>
          <div className="font-heading text-xl font-bold tracking-tight">Trébol · Álvarez Trucking</div>
        </div>

        {ok ? (
          <div className="rounded-lg border border-green-200 bg-green-50 p-6" data-testid="conexion-qbo-ok">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-8 w-8 shrink-0 text-green-600" />
              <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground">QuickBooks quedó conectado</h1>
            </div>
            {empresa && (
              <p className="mt-4 text-sm text-muted-foreground">
                Empresa autorizada: <b className="text-foreground" data-testid="conexion-qbo-empresa">{empresa}</b>
              </p>
            )}
            <p className="mt-3 text-sm text-muted-foreground">
              Listo, no hay que hacer nada más. Ya podés cerrar esta ventana y avisarle a quien te mandó el enlace
              que quedó conectado.
            </p>
          </div>
        ) : (
          <div className="rounded-lg border border-red-200 bg-red-50 p-6" data-testid="conexion-qbo-error">
            <div className="flex items-center gap-3">
              <XCircle className="h-8 w-8 shrink-0 text-red-600" />
              <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground">{m.titulo}</h1>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">{m.texto}</p>
            {m.detalle && (
              <p className="mt-3 break-all rounded-md border border-red-200 bg-white px-3 py-2 font-mono text-xs text-red-800"
                 data-testid="conexion-qbo-detalle">
                {m.detalle}
              </p>
            )}
          </div>
        )}

        <p className="mt-8 text-xs text-muted-foreground">
          Esta pantalla no muestra datos de QuickBooks ni de la plataforma; solo el resultado de la autorización.
          Si tenés usuario en la plataforma, podés <Link to="/login" className="text-primary underline-offset-4 hover:underline">iniciar sesión</Link>.
        </p>
        <p className="mt-6 border-t border-border pt-4 text-xs text-muted-foreground">
          Olimpo Web Design LLC · <a href={CONTACTO_URL} className="underline">{CONTACTO_URL}</a> ·{" "}
          <Link to="/terminos" className="underline">Términos</Link> · <Link to="/privacidad" className="underline">Privacidad</Link>
        </p>
      </div>
    </div>
  );
}
